/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../_metronic/layout/core/PageData'
import {useClientGetByIdMutation, useClientUpdateByIdMutation} from '../Client.api'
import {FormikProvider, useFormik} from 'formik'
import { preventEnterSubmit } from '../../../util/Functions'
import {IClientDto, IClientUpdateRequestDto} from '../Client.model'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { array, object, string } from 'yup'
import { COUNTRIES_EN, renderIdentifier, SECTORS } from '../../../util/Constants'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { Spinner } from '../../../common/component/Spinner'
import { IContactDto } from '../../contact/Contact.model'
import { ContactEditor } from '../../contact/components/ContactEditor'
import FormValidator from '../../../util/FormValidator.util'

interface IUpdateFormikSchema {
  id: string
  displayName: string
  sectorSelector: string
  sectorCode: string
  sector: string
  countryCode: string
  country: string
  address?: string
  latitude?: string
  longitude?: string
  contacts: IContactDto[]
}

const ClientEditPage = () => {
  const intl = useIntl()
  const formValidator = new FormValidator(intl)
  const navigate = useNavigate()
  const {clientId} = useParams()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.CLIENTS'}),
      path: '/clients',
      isSeparator: false,
      isActive: false,
    },
  ]

  const didFetchClient = useRef(false)
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  
  const [validationSchema, setValidationSchema] = useState(
    object().shape({
      id: string().required(),
      displayName: formValidator.validateStringStrict(),
      countryCode: formValidator.validateStringStrict(),
      sectorCode: string().notRequired(),
      sector: string().when('sectorCode', {
        is: (value) => value === 'OTHER',
        then: () => formValidator.validateStringStrict(),
      }),
      address: formValidator.validateString(),
      latitude: formValidator.validateString(),
      longitude: formValidator.validateString(),
      contacts: array().min(1, intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED_AT_LEAST'}, {min: 1}))
    })
  )
  const [initialValues, setInitialValues] = useState<IUpdateFormikSchema>({
    id: `${clientId}`,
    displayName: '',
    countryCode: '',
    country: '',
    sectorSelector: '',
    sectorCode: '',
    sector: '',
    address: '',
    latitude: '',
    longitude: '',
    contacts: [],
  })

  const [isClientLoading, setIsClientLoading] = useState(true)
  const [client, setClient] = useState<IClientDto>()
  
  const [clientGetById] = useClientGetByIdMutation()
  const [clientUpdateById] = useClientUpdateByIdMutation()

  const formik = useFormik<IUpdateFormikSchema>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        if (values) {
          setSubmitting(true)
          const request: IClientUpdateRequestDto = {
            id: values.id,
            displayName: values.displayName,
            sectorCode: values.sectorCode ?? '',
            sector: values.sectorCode === '' 
              ? '' 
              : values.sectorCode === 'OTHER'
                ? values.sector.trim().toLocaleUpperCase()
                : intl.formatMessage({id: `DROPDOWN.SECTOR.${values.sectorCode}`}),
            countryCode: values.countryCode,
            country: intl.formatMessage({id: `DROPDOWN.COUNTRY.${values.countryCode}`}),
            address: values.address,
            latitude: values.latitude,
            longitude: values.longitude,
            contacts: values.contacts,
          }

          const response: any = await clientUpdateById(request).unwrap()
          if (response && response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            navigate(`/clients/${response.id}`)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    const fetchClient = async (clientId) => {
      try {
        if (!didFetchClient.current) {
          setIsClientLoading(true)
          const response: any = await clientGetById({id: clientId})
          if (!response || response.error) {
            setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } else {
            const client: IClientDto = response.data
            setClient(client)

            formik.setFieldValue('displayName', client.displayName)
            formik.setFieldValue('sectorCode', client.sectorCode)
            formik.setFieldValue('sector', client.sector)
            formik.setFieldValue('countryCode', client.countryCode)
            formik.setFieldValue('country', client.country)
            formik.setFieldValue('address', client.address)
            formik.setFieldValue('latitude', client.latitude)
            formik.setFieldValue('longitude', client.longitude)
            formik.setFieldValue('contacts', client.contacts)
          }
        }
      } catch (e: any) {
        setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsClientLoading(false)
      }
    }

    if (clientId) {
      fetchClient(clientId)
    }
  }, [])

  const tabWithError = (tabId) => {
    switch (tabId) {
      case 'kt_tab_pane_1': {
        return (
          (formik.touched.id && formik.errors.id) ||
          (formik.touched.displayName && formik.errors.displayName) ||
          (formik.touched.countryCode && formik.errors.countryCode) ||
          (formik.touched.sectorCode && formik.errors.sectorCode) ||
          (formik.touched.address && formik.errors.address) ||
          (formik.touched.latitude && formik.errors.latitude) ||
          (formik.touched.longitude && formik.errors.longitude)
        )
      }
      case 'kt_tab_pane_2': {
        return formik.touched.contacts && formik.errors.contacts
      }
      default:
        return false
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
              <div className='d-flex flex-row'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      {client && (
                        <div className='d-flex flex-column'>
                          <span className='text-gray-800 fs-2 fw-bolder'>{client.displayName}</span>
                          <span className='fw-bolder text-primary me-1 fs-5'>
                            {renderIdentifier(client.sequence)}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className='d-flex flex-row-auto flex-start ms-5'>
                      {client && (
                        <>
                          <button
                            type='submit'
                            className='btn btn-sm btn-primary me-2 d-none d-md-block'
                            onClick={() => formik.handleSubmit()}
                            disabled={formik.isSubmitting}
                          >
                            {formik.isSubmitting ? (
                              <span className='indicator-progress' style={{display: 'block'}}>
                                {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            ) : (
                              <>
                                <i className='bi bi-floppy'></i>
                                <span className='indicator-label'>
                                  {intl.formatMessage({id: 'UTIL.SAVE'})}
                                </span>
                              </>
                            )}
                          </button>
                          <button
                            type='submit'
                            className='btn btn-sm btn-primary btn-active-color-secondary me-2 d-md-none'
                            onClick={() => formik.handleSubmit()}
                            disabled={formik.isSubmitting}
                          >
                            {formik.isSubmitting ? (
                              <span className='indicator-progress' style={{display: 'block'}}>
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            ) : (
                              <i className='bi bi-floppy'></i>
                            )}
                          </button>
                        </>
                      )}

                      {!formik.isSubmitting && (
                        <button
                          className='btn btn-sm btn-light'
                          onClick={() => navigate(`/clients/${clientId}`)}
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {isClientLoading && !formik.isSubmitting ? (
                <Spinner />
              ) : !client ? (
                <div className='row'>
                  <div className='d-flex flex-row-fluid flex-start mt-10'>
                    <span className='text-danger '>
                      <div className='d-flex flex-row justify-content-start align-items-center'>
                        <span className='fs-6 me-1'>
                          <i className='text-danger me-1 bi bi-exclamation-octagon' />
                          {intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'})}
                        </span>
                      </div>
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                    <li className='nav-item'>
                      <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                        {tabWithError('kt_tab_pane_1') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                      </a>
                    </li>
                    <li className='nav-item'>
                      <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                        {tabWithError('kt_tab_pane_2') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.CONTACTS'})}
                      </a>
                    </li>
                  </ul>
                  <div className='tab-content mt-10' id='myTabContent'>
                    <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                      <div className='row mb-5'>
                        <SubSectionHeader
                          title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                          separator={false}
                        />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.DISPLAY_NAME'})}
                              placeholder={intl.formatMessage({id: 'UTIL.DISPLAY_NAME'})}
                              required={true}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName={`displayName`}
                              enableInputLength={true}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.SECTOR'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='sectorCode'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('sectorCode')}
                                  onChange={(e) => {
                                    formik.handleChange(e)
                                    if (e.target.value === 'OTHER') {
                                      formik.setFieldValue('sector', '')
                                    }
                                  }}
                                  disabled={formik.isSubmitting}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {SECTORS &&
                                    SECTORS.map((sector, index) => (
                                      <option key={index} value={sector}>
                                        {intl.formatMessage({id: `DROPDOWN.SECTOR.${sector}`})}
                                      </option>
                                    ))}
                                </select>
                              }
                            ></CustomInputRenderer>

                            {formik.values.sectorCode === 'OTHER' && (
                              <CustomInputRenderer
                                placeholder={intl.formatMessage({id: 'UTIL.SPECIFY'})}
                                disabled={formik.isSubmitting}
                                formik={formik}
                                fieldName={`sector`}
                                enableInputLength={true}
                              ></CustomInputRenderer>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({id: 'UTIL.LOCATION'})}
                        />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName='countryCode'
                              object={
                                <select
                                  className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                  {...formik.getFieldProps('countryCode')}
                                  onChange={(e) => formik.handleChange(e)}
                                  disabled={formik.isSubmitting}
                                >
                                  <option value=''>
                                    {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                  </option>
                                  {COUNTRIES_EN &&
                                    COUNTRIES_EN.map((country, index) => (
                                      <option key={index} value={country.code}>
                                        {intl.formatMessage({
                                          id: `DROPDOWN.COUNTRY.${country.code}`,
                                        })}
                                      </option>
                                    ))}
                                </select>
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                              placeholder={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName={`address`}
                              enableInputLength={true}
                            ></CustomInputRenderer>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
                      <div className='row mb-5'>
                        <div className='px-5'>
                          <div className='row mb-5'>
                            <ContactEditor
                              formik={formik}
                              fieldName='contacts'
                              disabled={formik.isSubmitting}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  )
}

export {ClientEditPage}
